// TMOS Public Website Solutions Page
// Date: January 27, 2025
// Purpose: Solutions page with generic and industry-specific roadmaps

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicHeader } from '@/components/public/PublicHeader';
import { useAuth } from '@/state/auth/AuthProvider';
import publicApi from '@/services/publicApi';
import { useLanguage } from '@/state/language/LanguageProvider';

interface Solution {
    id: string;
    title: string;
    description?: string;
    image_url?: string;
    category: 'generic' | 'industry_specific';
    industry?: string;
    marketplace_template_id?: string;
}

export const SolutionsPage: React.FC = () => {
    const { t } = useLanguage();
    const [solutions, setSolutions] = useState<{
        generic: Solution[];
        industry_specific: Solution[];
    }>({ generic: [], industry_specific: [] });
    const [loading, setLoading] = useState(true);
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadSolutions();
    }, []);

    const loadSolutions = async () => {
        try {
            const response = await publicApi.get('/public/solutions/by-category');
            if (response.data.success) {
                setSolutions(response.data.data || { generic: [], industry_specific: [] });
            }
        } catch (error) {
            console.error('Error loading solutions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSolutionClick = (solution: Solution) => {
        if (solution.marketplace_template_id) {
            if (isAuthenticated) {
                navigate(`/marketplace/${solution.marketplace_template_id}`);
            } else {
                navigate('/', { state: { from: `/marketplace/${solution.marketplace_template_id}` } });
            }
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <PublicHeader />

            <main className="flex-1">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                    {/* Page Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-normal text-gray-900 mb-4">
                            {t('website.solutions.title')}
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            {t('website.solutions.subtitle')}
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5d2e]"></div>
                            <span className="ml-4 text-gray-600">{t('website.solutions.loading')}</span>
                        </div>
                    ) : (
                        <div className="space-y-12">
                            {/* Generic Solutions */}
                            {solutions.generic.length > 0 && (
                                <div>
                                    <h2 className="text-2xl font-normal text-gray-900 mb-6">{t('website.solutions.generic')}</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {solutions.generic.map((solution) => (
                                            <div
                                                key={solution.id}
                                                onClick={() => handleSolutionClick(solution)}
                                                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                                            >
                                                {solution.image_url && (
                                                    <img
                                                        src={solution.image_url}
                                                        alt={solution.title}
                                                        className="w-full h-48 object-cover"
                                                    />
                                                )}
                                                <div className="p-6">
                                                    <h3 className="text-xl font-normal text-gray-900 mb-2">
                                                        {solution.title}
                                                    </h3>
                                                    {solution.description && (
                                                        <p className="text-gray-600 mb-4">
                                                            {solution.description}
                                                        </p>
                                                    )}
                                                    <button className="text-[#1a5d2e] font-semibold hover:underline">
                                                        {t('website.solutions.view_roadmap')} →
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Industry-Specific Solutions */}
                            {solutions.industry_specific.length > 0 && (
                                <div>
                                    <h2 className="text-2xl font-normal text-gray-900 mb-6">{t('website.solutions.industry_specific')}</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {solutions.industry_specific.map((solution) => (
                                            <div
                                                key={solution.id}
                                                onClick={() => handleSolutionClick(solution)}
                                                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                                            >
                                                {solution.image_url && (
                                                    <img
                                                        src={solution.image_url}
                                                        alt={solution.title}
                                                        className="w-full h-48 object-cover"
                                                    />
                                                )}
                                                <div className="p-6">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h3 className="text-xl font-normal text-gray-900">
                                                            {solution.title}
                                                        </h3>
                                                        {solution.industry && (
                                                            <span className="px-3 py-1 text-xs font-semibold text-[#1a5d2e] bg-[#1a5d2e]/10 rounded-full">
                                                                {solution.industry}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {solution.description && (
                                                        <p className="text-gray-600 mb-4">
                                                            {solution.description}
                                                        </p>
                                                    )}
                                                    <button className="text-[#1a5d2e] font-semibold hover:underline">
                                                        {t('website.solutions.view_roadmap')} →
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {solutions.generic.length === 0 && solutions.industry_specific.length === 0 && (
                                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                                    <p className="text-gray-500">{t('website.solutions.no_solutions')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

        </div>
    );
};

